<!-- QR codes — Operations › QR codes. Layout from the Design-Component mock
     (scratchpad qr-canvas/project/Main.dc.html); the app's own sidebar and
     header replace the mock's nav rail and page header. -->

<div class="qr-page">
  <!-- Page actions: every card on the sheet. -->
  <div class="qr-page-actions">
    <button
      type="button"
      class="qr-btn qr-btn--surface"
      [disabled]="busy || cards.length === 0"
      (click)="printSheet()"
    >
      Print sheet
    </button>
    <button
      type="button"
      class="qr-btn qr-btn--solid"
      [disabled]="busy || cards.length === 0"
      (click)="downloadSheet()"
    >
      Download all
    </button>
  </div>

  @if (loading) {
    <div class="qr-state" role="status" aria-live="polite">
      <div class="qr-state-spinner" aria-hidden="true"></div>
      <div class="qr-state-title">Loading QR codes…</div>
    </div>
  } @else if (loadError) {
    <div class="qr-state qr-state--error" role="alert">
      <div class="qr-state-icon" aria-hidden="true"><i class="bx bx-error"></i></div>
      <div class="qr-state-title">QR codes could not be loaded</div>
      <p class="qr-state-text">{{ loadError }}</p>
      <button type="button" class="qr-btn qr-btn--solid" (click)="load()">Try again</button>
    </div>
  } @else {
    <!-- Summary strip -->
    <section class="qr-summary" aria-label="Sheet summary">
      <div class="qr-summary-row">
        <div class="qr-summary-eyebrow">Order types</div>
        <div class="qr-summary-pills">
          <span class="qr-order-pill">
            <span
              class="qr-order-dot"
              [class.qr-order-dot--off]="!orderTypes.dine_in"
              aria-hidden="true"
            ></span>
            Dine-in {{ orderTypes.dine_in ? 'on' : 'off' }}
          </span>
          <span class="qr-order-pill">
            <span
              class="qr-order-dot"
              [class.qr-order-dot--off]="!orderTypes.togo"
              aria-hidden="true"
            ></span>
            To-Go {{ orderTypes.togo ? 'on' : 'off' }}
          </span>
          <span class="qr-order-pill">
            <span
              class="qr-order-dot"
              [class.qr-order-dot--off]="!orderTypes.togo_dine_in"
              aria-hidden="true"
            ></span>
            To-Go dining {{ orderTypes.togo_dine_in ? 'on' : 'off' }}
          </span>
          <span class="qr-order-pill">
            <span
              class="qr-order-dot"
              [class.qr-order-dot--off]="!tableNumberRequired"
              aria-hidden="true"
            ></span>
            Table number {{ tableNumberRequired ? 'required' : 'optional' }}
          </span>
        </div>
      </div>
      <div class="qr-summary-stats">
        <div class="qr-stat">
          <div class="qr-stat-value">{{ cards.length }}</div>
          <div class="qr-stat-label">cards on the sheet</div>
        </div>
        <div class="qr-stat">
          <div class="qr-stat-value">{{ diningCards.length }}</div>
          <div class="qr-stat-label">To-Go dining tables</div>
        </div>
        <div class="qr-stat">
          <div class="qr-stat-value">{{ dineCards.length }}</div>
          <div class="qr-stat-label">
            Dine-in tables · {{ dineZoneCount }} {{ dineZoneCount === 1 ? 'zone' : 'zones' }}
          </div>
        </div>
        <div class="qr-stat">
          <div class="qr-stat-value">{{ togoCard ? 1 : 0 }}</div>
          <div class="qr-stat-label">To-Go counter code · POS: {{ posLabel }}</div>
        </div>
      </div>
    </section>

    <!-- Section pills -->
    <nav class="qr-section-nav" aria-label="Sheet sections">
      <button type="button" class="qr-section-pill" (click)="jumpTo('qr-togo')">
        To-Go counter <span class="qr-section-count">{{ togoCard ? 1 : 0 }}</span>
      </button>
      <button type="button" class="qr-section-pill" (click)="jumpTo('qr-dining')">
        To-Go dining <span class="qr-section-count">{{ diningCards.length }}</span>
      </button>
      <button type="button" class="qr-section-pill" (click)="jumpTo('qr-dinein')">
        Dine-in <span class="qr-section-count">{{ dineCards.length }}</span>
      </button>
    </nav>

    <div class="qr-columns">
      <div class="qr-main">
        <!-- 1. To-Go counter -->
        <section id="qr-togo" class="qr-card" aria-labelledby="qr-togo-h">
          <div class="qr-card-head">
            <div class="qr-card-titles">
              <h2 id="qr-togo-h" class="qr-card-title">To-Go counter</h2>
              <p class="qr-card-sub">
                The live To-Go code for your POS. To-Go dining without a table number rides this
                same code.
              </p>
            </div>
            <span class="qr-tag">{{ posLabel }} · {{ togoCard ? 'live' : 'no code' }}</span>
          </div>

          @if (togoCard) {
            <div class="qr-togo-row">
              <button
                type="button"
                class="qr-tile qr-tile--togo"
                [class.qr-tile--picked]="isPicked(togoCard)"
                [attr.aria-pressed]="isPicked(togoCard)"
                (click)="onTileClick(togoCard)"
              >
                @if (selectMode) {
                  <span class="qr-tile-check" aria-hidden="true">
                    <i class="bx" [class.bx-check]="selected.has(togoCard.key)"></i>
                  </span>
                }
                <img class="qr-tile-img" [src]="togoCard.spec.qrUrl" alt="QR code" />
                <span class="qr-tile-label">{{ togoCard.label }}</span>
                <span class="qr-tile-caption">{{ togoCard.caption }}</span>
              </button>
              <div class="qr-togo-notes">
                <div><strong>Opens</strong> the To-Go menu with pick-up at the counter.</div>
                <div>
                  <strong>Switching POS</strong> swaps this code for that provider's code; table
                  codes are re-made too.
                </div>
              </div>
            </div>
            <div class="qr-card-foot">
              <span class="qr-card-foot-note">1 card · 5 × 7 in</span>
              <div class="qr-card-foot-actions">
                <button
                  type="button"
                  class="qr-btn qr-btn--pill"
                  [disabled]="busy"
                  (click)="print([togoCard], 'TG')"
                >
                  Print
                </button>
                <button
                  type="button"
                  class="qr-btn qr-btn--pill"
                  [disabled]="busy"
                  (click)="download([togoCard], 'TG')"
                >
                  Download
                </button>
              </div>
            </div>
          } @else {
            <div class="qr-empty">
              <div class="qr-empty-icon" aria-hidden="true"><i class="bx bx-qr"></i></div>
              <div class="qr-empty-title">No To-Go code yet</div>
              <p class="qr-empty-text">
                The counter code is made when To-Go is switched on for your POS in Settings.
              </p>
            </div>
          }
        </section>

        <!-- 2. To-Go dining tables -->
        <section id="qr-dining" class="qr-card" aria-labelledby="qr-dining-h">
          <div class="qr-card-head">
            <div class="qr-card-titles">
              <h2 id="qr-dining-h" class="qr-card-title">To-Go dining tables</h2>
              <p class="qr-card-sub">
                Each table gets its own To-Go code. Scanning opens To-Go ordering with the table
                pre-filled and locked.
              </p>
            </div>
            @if (currentCount > 0) {
              <span class="qr-tag">TD-1 … TD-{{ currentCount }}</span>
            }
          </div>

          @if (diningOff) {
            <div class="qr-warning" role="status">
              <i class="bx bx-error" aria-hidden="true"></i>
              <div class="qr-warning-body">
                <strong>To-Go dining is off.</strong>
                <span>
                  Turn on To-Go dining and Table number required in Settings before printing table
                  codes. You can still generate them now.
                </span>
                @if (canOpenSettings) {
                  <a class="qr-warning-link" routerLink="/main/settings">Open Settings</a>
                }
              </div>
            </div>
          } @else if (!tableNumberRequired) {
            <div class="qr-warning" role="status">
              <i class="bx bx-error" aria-hidden="true"></i>
              <div class="qr-warning-body">
                <strong>Table number required is off,</strong>
                <span>so guests can order without a table.</span>
                @if (canOpenSettings) {
                  <a class="qr-warning-link" routerLink="/main/settings">Open Settings</a>
                }
              </div>
            </div>
          }

          <div class="qr-count">
            <label class="qr-count-label" for="qr-table-count">Number of tables</label>
            <div class="qr-stepper">
              <button
                type="button"
                class="qr-stepper-btn"
                aria-label="One table fewer"
                [disabled]="saving"
                (click)="stepCount(-1)"
              >
                −
              </button>
              <input
                id="qr-table-count"
                class="qr-stepper-input"
                type="text"
                inputmode="numeric"
                autocomplete="off"
                [attr.aria-invalid]="!countValid"
                [disabled]="saving"
                [(ngModel)]="countText"
                (ngModelChange)="onCountInput()"
                (keydown.enter)="submitCount()"
              />
              <button
                type="button"
                class="qr-stepper-btn"
                aria-label="One table more"
                [disabled]="saving"
                (click)="stepCount(1)"
              >
                +
              </button>
            </div>
            <span class="qr-count-hint" [class.qr-count-hint--invalid]="!countValid">
              @if (countValid) {
                1–{{ maxTables }} · Table 1 … Table {{ countText.trim() }}
              } @else {
                Enter a number from 1 to {{ maxTables }}
              }
            </span>
            <span class="qr-grow"></span>
            <button
              type="button"
              class="qr-btn qr-btn--solid"
              [disabled]="saving || !countValid"
              (click)="submitCount()"
            >
              {{ actionLabel }}
            </button>
          </div>

          @if (pending) {
            <div class="qr-confirm" role="dialog" aria-label="Confirm table count">
              <div class="qr-confirm-title">{{ pending.title }}</div>
              <div class="qr-chips">
                @for (chip of pending.chips; track chip.label) {
                  <span class="qr-chip" [class]="'qr-chip qr-chip--' + chip.tone">{{
                    chip.label
                  }}</span>
                }
              </div>
              <p
                class="qr-confirm-body"
                [class.qr-confirm-body--remove]="pending.kind === 'remove'"
              >
                {{ pending.body }}
              </p>
              <div class="qr-confirm-actions">
                <button
                  type="button"
                  class="qr-btn qr-btn--pill"
                  [disabled]="saving"
                  (click)="cancelCount()"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  class="qr-btn qr-btn--solid"
                  [disabled]="saving"
                  (click)="applyCount()"
                >
                  {{ saving ? 'Saving…' : pending.confirm }}
                </button>
              </div>
            </div>
          }

          @if (diningCards.length > 0) {
            <div class="qr-grid">
              @for (card of diningCards; track card.key) {
                <button
                  type="button"
                  class="qr-tile"
                  [class.qr-tile--picked]="isPicked(card)"
                  [attr.aria-pressed]="isPicked(card)"
                  (click)="onTileClick(card)"
                >
                  @if (selectMode) {
                    <span class="qr-tile-check" aria-hidden="true">
                      <i class="bx" [class.bx-check]="selected.has(card.key)"></i>
                    </span>
                  }
                  <img class="qr-tile-img" [src]="card.spec.qrUrl" alt="QR code" />
                  <span class="qr-tile-label">{{ card.label }}</span>
                  <span class="qr-tile-caption">{{ card.caption }}</span>
                </button>
              }
            </div>
            <div class="qr-card-foot">
              <span class="qr-card-foot-note">
                {{ cardCount(diningCards.length) }} · 5 × 7 in · one per page
              </span>
              <div class="qr-card-foot-actions">
                <button
                  type="button"
                  class="qr-btn qr-btn--pill"
                  [disabled]="busy"
                  (click)="print(diningCards, 'To-Go dining')"
                >
                  Print all
                </button>
                <button
                  type="button"
                  class="qr-btn qr-btn--pill"
                  [disabled]="busy"
                  (click)="download(diningCards, 'To-Go dining')"
                >
                  Download all
                </button>
              </div>
            </div>
          } @else {
            <div class="qr-empty">
              <div class="qr-empty-icon" aria-hidden="true"><i class="bx bx-qr"></i></div>
              <div class="qr-empty-title">No table codes yet</div>
              <p class="qr-empty-text">
                Choose how many tables are set up for To-Go dining and press Generate. Cards appear
                here, ready to print.
              </p>
            </div>
          }
        </section>

        <!-- 3. Dine-in tables -->
        <section id="qr-dinein" class="qr-card" aria-labelledby="qr-dinein-h">
          <div class="qr-card-head">
            <div class="qr-card-titles">
              <h2 id="qr-dinein-h" class="qr-card-title">Dine-in tables</h2>
              <p class="qr-card-sub">
                One code per table from the Tables page, grouped by zone. Edit tables there.
              </p>
            </div>
            <a class="qr-link" routerLink="/main/table">Manage tables</a>
          </div>

          @if (dineZones.length > 0) {
            @for (zone of dineZones; track zone.zone) {
              <div class="qr-zone">
                <div class="qr-zone-label">
                  {{ zone.zone || 'No zone' }}
                  <span class="qr-zone-rule" aria-hidden="true"></span>
                </div>
                <div class="qr-grid">
                  @for (card of zone.cards; track card.key) {
                    <button
                      type="button"
                      class="qr-tile"
                      [class.qr-tile--picked]="isPicked(card)"
                      [attr.aria-pressed]="isPicked(card)"
                      (click)="onTileClick(card)"
                    >
                      @if (selectMode) {
                        <span class="qr-tile-check" aria-hidden="true">
                          <i class="bx" [class.bx-check]="selected.has(card.key)"></i>
                        </span>
                      }
                      <img class="qr-tile-img" [src]="card.spec.qrUrl" alt="QR code" />
                      <span class="qr-tile-label">{{ card.label }}</span>
                      <span class="qr-tile-caption">{{ card.caption }}</span>
                    </button>
                  }
                </div>
              </div>
            }
            <div class="qr-card-foot">
              <span class="qr-card-foot-note">
                {{ cardCount(dineCards.length) }} · 5 × 7 in · one per page
              </span>
              <div class="qr-card-foot-actions">
                <button
                  type="button"
                  class="qr-btn qr-btn--pill"
                  [disabled]="busy"
                  (click)="print(dineCards, 'Dine-in')"
                >
                  Print all
                </button>
                <button
                  type="button"
                  class="qr-btn qr-btn--pill"
                  [disabled]="busy"
                  (click)="download(dineCards, 'Dine-in')"
                >
                  Download all
                </button>
              </div>
            </div>
          } @else {
            <div class="qr-empty">
              <div class="qr-empty-icon" aria-hidden="true"><i class="bx bx-qr"></i></div>
              <div class="qr-empty-title">No dine-in codes yet</div>
              <p class="qr-empty-text">
                Each table you add on the Tables page gets a dine-in code and shows up here.
              </p>
              <a class="qr-btn qr-btn--pill" routerLink="/main/table">Go to Tables</a>
            </div>
          }
        </section>
      </div>

      <!-- Right column: print card -->
      <aside class="qr-card qr-aside" aria-labelledby="qr-print-h">
        <div class="qr-aside-head">
          <h2 id="qr-print-h" class="qr-card-title">Print card · 5 × 7 in</h2>
          @if (previewUrl && !selectMode) {
            <a
              class="qr-icon-btn"
              [href]="previewUrl"
              target="_blank"
              rel="noopener"
              aria-label="Open full view"
            >
              <i class="bx bx-expand-alt" aria-hidden="true"></i>
            </a>
          }
        </div>

        @if (selectMode) {
          <div class="qr-select-panel">
            <div class="qr-select-count">
              {{ cardCount(selected.size) }} selected
              <span class="qr-select-hint"
                >Tick cards on the sheet to print or download a mix.</span
              >
            </div>
            <div class="qr-select-links">
              <button type="button" class="qr-link-btn" (click)="selectAll()">Select all</button>
              <button
                type="button"
                class="qr-link-btn"
                [disabled]="selected.size === 0"
                (click)="clearSelection()"
              >
                Clear
              </button>
            </div>
          </div>
          <div class="qr-aside-actions">
            <button
              type="button"
              class="qr-btn qr-btn--pill qr-btn--grow"
              [disabled]="busy || selected.size === 0"
              (click)="downloadSelected()"
            >
              Download {{ selected.size || '' }}
            </button>
            <button
              type="button"
              class="qr-btn qr-btn--solid qr-btn--grow"
              [disabled]="busy || selected.size === 0"
              (click)="printSelected()"
            >
              Print {{ selected.size || '' }}
            </button>
          </div>
          <button type="button" class="qr-btn qr-btn--dashed" (click)="toggleSelectMode()">
            Done selecting
          </button>
        } @else {
          <div class="qr-preview" [class.qr-preview--busy]="previewBusy">
            @if (previewUrl) {
              <img
                class="qr-preview-img"
                [src]="previewUrl"
                [alt]="pickedCopy ? pickedCopy.headline + ' card' : 'Card preview'"
              />
            } @else if (previewBusy) {
              <div class="qr-preview-empty" role="status">Drawing the card…</div>
            } @else {
              <div class="qr-preview-empty">Pick a code on the sheet to preview its card.</div>
            }
          </div>

          @if (pickedCopy) {
            <div class="qr-preview-meta">
              <div class="qr-preview-name">{{ pickedCopy.headline }} · {{ pickedKindLabel }}</div>
              <div class="qr-preview-spec">
                {{ pickedCopy.code }} · 1500 × 2100 px · 300 dpi · PNG
              </div>
            </div>
          }
          <div class="qr-aside-actions">
            <button
              type="button"
              class="qr-btn qr-btn--pill qr-btn--grow"
              [disabled]="busy || !pickedCard"
              (click)="downloadPicked()"
            >
              Download
            </button>
            <button
              type="button"
              class="qr-btn qr-btn--solid qr-btn--grow"
              [disabled]="busy || !pickedCard"
              (click)="printPicked()"
            >
              Print
            </button>
          </div>
          <button
            type="button"
            class="qr-btn qr-btn--dashed"
            [disabled]="cards.length === 0"
            (click)="toggleSelectMode()"
          >
            Select cards to print or download a mix
          </button>
        }
        <p class="qr-aside-note">
          Print opens one 5 × 7 page per card. Allow pop-ups for this site.
        </p>
      </aside>
    </div>
  }
</div>
